import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { 
  Laptop, 
  Play, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Camera,
  Battery,
  Monitor,
  Keyboard,
  Speaker,
  Usb,
  HardDrive,
  Cpu,
  Thermometer,
  Settings,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Search,
  Plus,
  Eye,
  Clock,
  User,
  MapPin
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Zobox Brand Colors
const ZOBOX_BLUE = '#2b3380';
const ZOBOX_ORANGE = '#ff6b35';

// Module icons mapping
const MODULE_ICONS = {
  device_identification: Settings,
  hardware_config: Cpu,
  battery_health: Battery,
  battery_stress: Battery,
  display: Monitor,
  keyboard: Keyboard,
  trackpad: Settings,
  audio: Speaker,
  webcam: Camera,
  ports: Usb,
  storage_smart: HardDrive,
  cpu_ram: Cpu,
  gpu_stress: Monitor,
  thermal_fan: Thermometer,
  bios_cmos: Settings,
  cosmetic: Eye,
  photos: Camera
};

// Result badge component
const ResultBadge = ({ result }) => {
  const styles = {
    PASS: 'bg-green-100 text-green-800 border-green-200',
    FAIL: 'bg-red-100 text-red-800 border-red-200',
    NOT_TESTED: 'bg-gray-100 text-gray-600 border-gray-200',
    RECORDED: 'bg-blue-100 text-blue-800 border-blue-200',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[result] || styles.NOT_TESTED}`}>
      {result?.replace('_', ' ') || 'Pending'}
    </span>
  );
};

// Grade badge component
const GradeBadge = ({ grade }) => {
  const gradeColors = {
    Z1: 'bg-green-500',
    Z2: 'bg-green-400',
    Z3: 'bg-yellow-400',
    Z4: 'bg-yellow-500',
    Z5: 'bg-orange-400',
    Z6: 'bg-orange-500',
    Z7: 'bg-red-500',
  };

  if (!grade) return null;

  return (
    <span className={`px-3 py-1 text-white text-sm font-bold rounded ${gradeColors[grade] || 'bg-gray-500'}`}>
      {grade}
    </span>
  );
};

const LaptopIQC = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [modules, setModules] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // New IQC form
  const [showNewIQC, setShowNewIQC] = useState(false);
  const [newIQCForm, setNewIQCForm] = useState({
    serial_number: '',
    did: '',
    operator_id: user?.employee_id || 'OP001',
    operator_name: user?.name || 'TRC Operator',
    qc_location: 'TRC-001'
  });
  
  // Active IQC session
  const [activeSession, setActiveSession] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);
  const [moduleForm, setModuleForm] = useState({
    result: 'PASS',
    fail_reasons: [],
    observations: [],
    notes: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/trc/laptop-iqc/dashboard/stats`, { headers });
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  // Fetch sessions list
  const fetchSessions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/trc/laptop-iqc/sessions?limit=50`, { headers });
      if (response.data.success) {
        setSessions(response.data.sessions || []);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  }, []);

  // Fetch QC modules
  const fetchModules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/trc/laptop-iqc/modules`, { headers });
      if (response.data.success) {
        setModules(response.data);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchSessions(), fetchModules()]);
      setLoading(false);
    };
    loadData();
  }, [fetchStats, fetchSessions, fetchModules]);

  // Start new IQC
  const handleStartIQC = async () => {
    if (!newIQCForm.serial_number) {
      alert('Please enter Serial Number');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/api/trc/laptop-iqc/start`,
        newIQCForm,
        { headers }
      );

      if (response.data.success) {
        // Fetch the created session
        const sessionRes = await axios.get(
          `${API}/api/trc/laptop-iqc/session/${response.data.session_id}`,
          { headers }
        );
        
        setActiveSession(sessionRes.data.session);
        setShowNewIQC(false);
        setActiveTab('execute');
        fetchSessions();
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to start IQC');
    }
  };

  // Submit module result
  const handleSubmitModule = async () => {
    if (!activeSession || !currentModule) return;

    try {
      const response = await axios.post(
        `${API}/api/trc/laptop-iqc/module/submit`,
        {
          session_id: activeSession.session_id,
          module_name: currentModule,
          ...moduleForm
        },
        { headers }
      );

      if (response.data.success) {
        // Refresh session
        const sessionRes = await axios.get(
          `${API}/api/trc/laptop-iqc/session/${activeSession.session_id}`,
          { headers }
        );
        setActiveSession(sessionRes.data.session);
        setCurrentModule(null);
        setModuleForm({ result: 'PASS', fail_reasons: [], observations: [], notes: '' });
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit module');
    }
  };

  // Complete IQC
  const handleCompleteIQC = async () => {
    if (!activeSession) return;

    try {
      const response = await axios.post(
        `${API}/api/trc/laptop-iqc/complete`,
        { session_id: activeSession.session_id },
        { headers }
      );

      if (response.data.success) {
        alert(`IQC Completed!\n\nGrade: ${response.data.iqc_grade}\nDID: ${response.data.did || 'N/A'}\nResult: ${response.data.overall_result}`);
        setActiveSession(null);
        setActiveTab('dashboard');
        fetchStats();
        fetchSessions();
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to complete IQC');
    }
  };

  // View session details
  const handleViewSession = async (sessionId) => {
    try {
      const response = await axios.get(
        `${API}/api/trc/laptop-iqc/session/${sessionId}`,
        { headers }
      );
      if (response.data.success) {
        setSelectedSession(response.data.session);
      }
    } catch (error) {
      alert('Failed to load session details');
    }
  };

  // Render dashboard tab
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="stat-today-total">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Today's IQC</p>
              <p className="text-3xl font-bold" style={{ color: ZOBOX_BLUE }}>{stats?.today?.total || 0}</p>
            </div>
            <Laptop className="w-10 h-10 text-gray-300" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="stat-today-passed">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Passed</p>
              <p className="text-3xl font-bold text-green-600">{stats?.today?.passed || 0}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-200" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="stat-today-failed">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Failed</p>
              <p className="text-3xl font-bold text-red-600">{stats?.today?.failed || 0}</p>
            </div>
            <XCircle className="w-10 h-10 text-red-200" />
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="stat-pending">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending</p>
              <p className="text-3xl font-bold text-orange-600">{stats?.pending_iqc || 0}</p>
            </div>
            <AlertCircle className="w-10 h-10 text-orange-200" />
          </div>
        </div>
      </div>

      {/* Pass Rate & Grade Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Today's Pass Rate</h3>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-full border-8 border-green-500 flex items-center justify-center">
              <span className="text-2xl font-bold">{stats?.today?.pass_rate || 0}%</span>
            </div>
            <div className="text-sm text-gray-500">
              <p>{stats?.today?.passed || 0} passed out of {stats?.today?.completed || 0} completed</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Grade Distribution</h3>
          <div className="flex flex-wrap gap-2">
            {['Z1', 'Z2', 'Z3', 'Z4', 'Z5', 'Z6', 'Z7'].map(grade => (
              <div key={grade} className="flex items-center gap-2">
                <GradeBadge grade={grade} />
                <span className="text-sm text-gray-600">
                  {stats?.grade_distribution?.[grade] || 0}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="bg-white rounded-xl shadow-sm border">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Recent IQC Sessions</h3>
          <button
            onClick={() => setShowNewIQC(true)}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg"
            style={{ backgroundColor: ZOBOX_ORANGE }}
            data-testid="start-new-iqc-btn"
          >
            <Plus className="w-4 h-4" />
            Start New IQC
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serial Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Operator</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sessions.slice(0, 10).map((session) => (
                <tr key={session.session_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-mono">{session.session_id}</td>
                  <td className="px-4 py-3 text-sm">{session.serial_number}</td>
                  <td className="px-4 py-3 text-sm font-mono">{session.did || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      session.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                      session.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {session.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <GradeBadge grade={session.iqc_grade} />
                  </td>
                  <td className="px-4 py-3 text-sm">{session.operator_name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(session.started_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleViewSession(session.session_id)}
                      className="text-blue-600 hover:text-blue-800"
                      data-testid={`view-session-${session.session_id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {sessions.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No IQC sessions found. Click "Start New IQC" to begin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // Render IQC execution
  const renderExecution = () => {
    if (!activeSession) {
      return (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <Laptop className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Active IQC Session</h3>
          <p className="text-gray-500 mb-4">Start a new IQC session from the dashboard</p>
          <button
            onClick={() => { setShowNewIQC(true); setActiveTab('dashboard'); }}
            className="px-6 py-2 text-white rounded-lg"
            style={{ backgroundColor: ZOBOX_ORANGE }}
          >
            Start New IQC
          </button>
        </div>
      );
    }

    const completedModules = Object.keys(activeSession.modules || {});
    const allModules = modules?.modules || {};

    return (
      <div className="space-y-6">
        {/* Session Header */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold" style={{ color: ZOBOX_BLUE }}>
                {activeSession.session_id}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Serial: {activeSession.serial_number} | DID: {activeSession.did || 'Pending'}
              </p>
              {activeSession.zupc && (
                <p className="text-sm text-gray-500">ZUPC: {activeSession.zupc}</p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-gray-500">Progress</p>
                <p className="text-lg font-bold">
                  {completedModules.length} / {Object.keys(allModules).length}
                </p>
              </div>
              <button
                onClick={handleCompleteIQC}
                disabled={completedModules.length < Object.keys(allModules).length}
                className={`px-6 py-2 rounded-lg font-medium ${
                  completedModules.length >= Object.keys(allModules).length
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                data-testid="complete-iqc-btn"
              >
                Complete IQC
              </button>
            </div>
          </div>
        </div>

        {/* Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(allModules).map(([key, module]) => {
            const Icon = MODULE_ICONS[key] || Settings;
            const isCompleted = completedModules.includes(key);
            const result = activeSession.modules?.[key]?.result;

            return (
              <div
                key={key}
                className={`bg-white rounded-xl shadow-sm border p-4 cursor-pointer hover:shadow-md transition-shadow ${
                  currentModule === key ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => !isCompleted && setCurrentModule(key)}
                data-testid={`module-card-${key}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      isCompleted 
                        ? result === 'PASS' ? 'bg-green-100' : result === 'FAIL' ? 'bg-red-100' : 'bg-blue-100'
                        : 'bg-gray-100'
                    }`}>
                      <Icon className={`w-5 h-5 ${
                        isCompleted
                          ? result === 'PASS' ? 'text-green-600' : result === 'FAIL' ? 'text-red-600' : 'text-blue-600'
                          : 'text-gray-500'
                      }`} />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{module.name}</h4>
                      <p className="text-xs text-gray-500">{module.type}</p>
                    </div>
                  </div>
                  {isCompleted ? (
                    <ResultBadge result={result} />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Module Form Modal */}
        {currentModule && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold" style={{ color: ZOBOX_BLUE }}>
                  {allModules[currentModule]?.name}
                </h3>
                <p className="text-sm text-gray-500">{allModules[currentModule]?.description}</p>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Result Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Result</label>
                  <div className="flex gap-4">
                    {['PASS', 'FAIL', 'NOT_TESTED'].map((result) => (
                      <button
                        key={result}
                        onClick={() => setModuleForm({ ...moduleForm, result })}
                        className={`px-4 py-2 rounded-lg border-2 font-medium ${
                          moduleForm.result === result
                            ? result === 'PASS' ? 'border-green-500 bg-green-50 text-green-700'
                            : result === 'FAIL' ? 'border-red-500 bg-red-50 text-red-700'
                            : 'border-gray-500 bg-gray-50 text-gray-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {result.replace('_', ' ')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fail Reasons (if FAIL selected) */}
                {moduleForm.result === 'FAIL' && allModules[currentModule]?.fail_reasons && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Fail Reasons</label>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {allModules[currentModule].fail_reasons.map((reason, idx) => (
                        <label key={idx} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={moduleForm.fail_reasons.includes(reason)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setModuleForm({
                                  ...moduleForm,
                                  fail_reasons: [...moduleForm.fail_reasons, reason]
                                });
                              } else {
                                setModuleForm({
                                  ...moduleForm,
                                  fail_reasons: moduleForm.fail_reasons.filter(r => r !== reason)
                                });
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm">{reason}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                  <textarea
                    value={moduleForm.notes}
                    onChange={(e) => setModuleForm({ ...moduleForm, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Any additional observations..."
                  />
                </div>
              </div>

              <div className="p-6 border-t flex justify-end gap-3">
                <button
                  onClick={() => setCurrentModule(null)}
                  className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitModule}
                  className="px-6 py-2 text-white rounded-lg"
                  style={{ backgroundColor: ZOBOX_BLUE }}
                  data-testid="submit-module-btn"
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // New IQC Modal
  const renderNewIQCModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold" style={{ color: ZOBOX_BLUE }}>
            Start New Laptop IQC
          </h3>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number *</label>
            <input
              type="text"
              value={newIQCForm.serial_number}
              onChange={(e) => setNewIQCForm({ ...newIQCForm, serial_number: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter laptop serial number"
              data-testid="serial-number-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DID (Optional)</label>
            <input
              type="text"
              value={newIQCForm.did}
              onChange={(e) => setNewIQCForm({ ...newIQCForm, did: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="If device already has DID"
              data-testid="did-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">QC Location</label>
            <select
              value={newIQCForm.qc_location}
              onChange={(e) => setNewIQCForm({ ...newIQCForm, qc_location: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="TRC-001">TRC-001 (Main)</option>
              <option value="TRC-002">TRC-002</option>
              <option value="TRC-003">TRC-003</option>
            </select>
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3">
          <button
            onClick={() => setShowNewIQC(false)}
            className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleStartIQC}
            className="px-6 py-2 text-white rounded-lg"
            style={{ backgroundColor: ZOBOX_ORANGE }}
            data-testid="confirm-start-iqc-btn"
          >
            Start IQC
          </button>
        </div>
      </div>
    </div>
  );

  // Session detail modal
  const renderSessionDetail = () => {
    if (!selectedSession) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold" style={{ color: ZOBOX_BLUE }}>
                {selectedSession.session_id}
              </h3>
              <p className="text-sm text-gray-500">
                Serial: {selectedSession.serial_number} | DID: {selectedSession.did || 'N/A'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <GradeBadge grade={selectedSession.iqc_grade} />
              <ResultBadge result={selectedSession.overall_result} />
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <p className="font-medium">{selectedSession.status}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">ZUPC</p>
                <p className="font-medium">{selectedSession.zupc || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Operator</p>
                <p className="font-medium">{selectedSession.operator_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Location</p>
                <p className="font-medium">{selectedSession.qc_location}</p>
              </div>
            </div>

            {/* Module Results */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Module Results</h4>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(selectedSession.modules || {}).map(([key, data]) => (
                  <div key={key} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm">{modules?.modules?.[key]?.name || key}</span>
                    <ResultBadge result={data.result} />
                  </div>
                ))}
              </div>
            </div>

            {/* Photos */}
            {selectedSession.photos?.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Photos ({selectedSession.photos.length}/7)</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedSession.photos.map((photo, idx) => (
                    <div key={idx} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {photo.panel_name}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-6 border-t flex justify-end">
            <button
              onClick={() => setSelectedSession(null)}
              className="px-6 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="laptop-iqc-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: ZOBOX_BLUE }}>
              Laptop IQC
            </h1>
            <p className="text-sm text-gray-500">Inward Quality Check for Laptops at TRC</p>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeTab === 'dashboard'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={activeTab === 'dashboard' ? { backgroundColor: ZOBOX_BLUE } : {}}
              data-testid="tab-dashboard"
            >
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('execute')}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeTab === 'execute'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={activeTab === 'execute' ? { backgroundColor: ZOBOX_BLUE } : {}}
              data-testid="tab-execute"
            >
              Execute IQC
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'execute' && renderExecution()}

        {/* Modals */}
        {showNewIQC && renderNewIQCModal()}
        {selectedSession && renderSessionDetail()}
      </div>
    </Layout>
  );
};

export default LaptopIQC;
